/**
 * Export Utilities - CSV, Excel, JSON, PDF generation
 * 
 * Comprehensive export functionality for entities and reports
 * Full implementation with xlsx for Excel and jsPDF for PDF
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { detectFieldType } from './entityFieldFormatter';

/**
 * Export data to CSV (no formatting, plain text)
 */
export const exportToCSV = (data: any[], filename: string = 'export.csv'): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get all unique keys from data
  const headers = Array.from(
    new Set(data.flatMap((row) => Object.keys(row)))
  );

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          
          // Handle null/undefined
          if (value === null || value === undefined) {
            return '';
          }
          
          // Handle arrays and objects - convert to JSON string
          if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
            return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          }
          
          // Handle booleans
          if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
          }
          
          // Handle strings that contain commas or quotes
          if (typeof value === 'string') {
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }
          
          // Numbers and other types
          return String(value);
        })
        .join(',')
    ),
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Export data to JSON
 */
export const exportToJSON = (data: any, filename: string = 'export.json', pretty: boolean = true): void => {
  const jsonContent = JSON.stringify(data, null, pretty ? 2 : 0);
  
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Get color for enum values (similar to getEnumColorClass in entityFieldFormatter)
 */
function getEnumColor(key: string, value: string): { bg: string; text: string } | null {
  const lowerKey = key.toLowerCase();
  const lowerValue = value.toLowerCase();

  // Status-based colors
  if (lowerKey.includes('status') || lowerKey.includes('validation')) {
    if (lowerValue.includes('valid') || lowerValue.includes('complete') || lowerValue === 'yes') {
      return { bg: 'C6EFCE', text: '006100' }; // Green
    }
    if (lowerValue.includes('fail') || lowerValue.includes('error') || lowerValue === 'no') {
      return { bg: 'FFC7CE', text: '9C0006' }; // Red
    }
    if (lowerValue.includes('review') || lowerValue.includes('pending') || lowerValue.includes('warning')) {
      return { bg: 'FFEB9C', text: '9C6500' }; // Yellow
    }
    if (lowerValue.includes('suspicious')) {
      return { bg: 'FFC7CE', text: '9C0006' }; // Orange/Red
    }
  }

  // Type-based colors
  if (lowerKey.includes('type') || lowerKey.includes('matrix')) {
    const typeColors: Record<string, { bg: string; text: string }> = {
      'groundwater': { bg: 'D9E1F2', text: '1F4E78' }, // Blue
      'surface_water': { bg: 'E7F6F8', text: '0C7CD5' }, // Cyan
      'soil': { bg: 'FFF2CC', text: 'BF8F00' }, // Amber
      'sediment': { bg: 'FFEB9C', text: '9C6500' }, // Yellow
      'tissue': { bg: 'FCE4D6', text: 'C65911' }, // Pink
      'tap_water': { bg: 'E2EFDA', text: '70AD47' }, // Indigo/Green
    };
    return typeColors[lowerValue] || { bg: 'F2F2F2', text: '595959' }; // Gray default
  }

  // Source-based colors
  if (lowerKey.includes('source')) {
    if (lowerValue.includes('raw') || lowerValue.includes('explicit')) {
      return { bg: 'C6EFCE', text: '006100' }; // Green
    }
    if (lowerValue.includes('processed')) {
      return { bg: 'FFEB9C', text: '9C6500' }; // Yellow
    }
    if (lowerValue.includes('report')) {
      return { bg: 'FFC7CE', text: '9C0006' }; // Orange/Red
    }
    if (lowerValue === 'none') {
      return { bg: 'F2F2F2', text: '595959' }; // Gray
    }
  }

  // Default enum color
  return { bg: 'D9E1F2', text: '1F4E78' }; // Light blue
}

/**
 * Create a formatted worksheet with conditional formatting, bold headers, and alternating rows
 * 
 * NOTE: The free version of xlsx (SheetJS) does NOT support cell styling.
 * Styles will be applied but may not render in the free version.
 * For full styling support, consider upgrading to xlsx Pro or using ExcelJS.
 */
function createFormattedWorksheet(
  data: any[],
  fieldMetadata: Array<{ key: string; type: string; isBoolean?: boolean; isEnum?: boolean; isNumeric?: boolean }>
): XLSX.WorkSheet {
  // Create worksheet from data
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Get range
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const headers = Object.keys(data[0] || {});
  
  // Create metadata lookup
  const metadataMap = new Map(fieldMetadata.map(m => [m.key, m]));
  
  // Try to apply styles (may not work in free xlsx version)
  // Style header row (row 0)
  for (let col = 0; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[cellAddress]) continue;
    
    // Attempt to apply header styling (may be ignored by free xlsx)
    try {
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } }, // Blue header background
        alignment: { horizontal: 'left', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
    } catch (e) {
      // Style not supported - continue without styling
      console.debug('Cell styling not supported by xlsx library version');
    }
  }
  
  // Style data rows with alternating colors and conditional formatting
  for (let row = 1; row <= range.e.r; row++) {
    const isEvenRow = row % 2 === 0;
    const rowBgColor = isEvenRow ? 'F2F2F2' : 'FFFFFF'; // Light gray for even rows
    
    for (let col = 0; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (!worksheet[cellAddress]) continue;
      
      const headerKey = headers[col];
      const metadata = metadataMap.get(headerKey);
      const cellValue = worksheet[cellAddress].v;
      
      // Base style
      const cellStyle: any = {
        fill: { fgColor: { rgb: rowBgColor } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'E0E0E0' } },
          bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
          left: { style: 'thin', color: { rgb: 'E0E0E0' } },
          right: { style: 'thin', color: { rgb: 'E0E0E0' } }
        }
      };
      
      // Apply conditional formatting based on field type
      if (metadata) {
        if (metadata.isBoolean) {
          // Boolean: Green for true, Red for false
          if (cellValue === true || cellValue === 'true' || cellValue === 'Yes') {
            cellStyle.fill = { fgColor: { rgb: 'C6EFCE' } }; // Light green
            cellStyle.font = { color: { rgb: '006100' }, bold: true }; // Dark green text
          } else if (cellValue === false || cellValue === 'false' || cellValue === 'No') {
            cellStyle.fill = { fgColor: { rgb: 'FFC7CE' } }; // Light red
            cellStyle.font = { color: { rgb: '9C0006' }, bold: true }; // Dark red text
          }
        } else if (metadata.isEnum) {
          // Enum: Color based on value
          const enumColor = getEnumColor(headerKey, String(cellValue));
          if (enumColor) {
            cellStyle.fill = { fgColor: { rgb: enumColor.bg } };
            cellStyle.font = { color: { rgb: enumColor.text }, bold: true };
          }
        } else if (metadata.isNumeric) {
          // Numeric: Blue text, right-aligned
          cellStyle.font = { color: { rgb: '0066CC' }, bold: true };
          cellStyle.alignment = { horizontal: 'right', vertical: 'center' };
        }
      }
      
      // Try to apply style (may be ignored)
      try {
        worksheet[cellAddress].s = cellStyle;
      } catch (e) {
        // Style not supported - continue without styling
      }
    }
  }
  
  return worksheet;
}

/**
 * Export data to Excel using xlsx library
 * Full Excel support with proper formatting
 */
export const exportToExcel = (
  data: any[],
  filename: string = 'export.xlsx',
  options?: {
    sheetName?: string;
    columnWidths?: number[];
    autoFilter?: boolean;
    freezeHeader?: boolean;
    enhancedFormatting?: boolean;
    fieldMetadata?: Array<{ key: string; type: string; isBoolean?: boolean; isEnum?: boolean; isNumeric?: boolean }>;
  }
): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const { 
    sheetName = 'Sheet1', 
    columnWidths, 
    autoFilter = true, 
    freezeHeader = true,
    enhancedFormatting = false,
    fieldMetadata = []
  } = options || {};

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  
  if (enhancedFormatting && fieldMetadata.length > 0) {
    // Use enhanced formatting
    const worksheet = createFormattedWorksheet(data, fieldMetadata);
    
    // Apply column widths if provided
    if (columnWidths && columnWidths.length > 0) {
      worksheet['!cols'] = columnWidths.map((width) => ({ wch: width }));
    } else {
      // Auto-calculate column widths
      const headers = Object.keys(data[0] || {});
      worksheet['!cols'] = headers.map((header) => {
        const maxLength = Math.max(
          header.length,
          ...data.map((row) => String(row[header] || '').length).slice(0, 100)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });
    }

    // Add autofilter
    if (autoFilter && data.length > 0) {
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
    }

    // Freeze header row
    if (freezeHeader) {
      worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  } else {
    // Standard export without enhanced formatting
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Apply column widths if provided
    if (columnWidths && columnWidths.length > 0) {
      worksheet['!cols'] = columnWidths.map((width) => ({ wch: width }));
    } else {
      // Auto-calculate column widths based on content
      const headers = Object.keys(data[0] || {});
      worksheet['!cols'] = headers.map((header) => {
        const maxLength = Math.max(
          header.length,
          ...data.map((row) => String(row[header] || '').length).slice(0, 100) // Check first 100 rows
        );
        return { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
      });
    }

    // Add autofilter
    if (autoFilter && data.length > 0) {
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
    }

    // Freeze header row
    if (freezeHeader) {
      worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  // Generate and download
  XLSX.writeFile(workbook, filename);
};

/**
 * Export to Excel with enhanced formatting (bold headers, alternating rows, conditional formatting)
 * Automatically detects field types and applies appropriate formatting
 */
export const exportToExcelFormatted = (
  data: any[],
  filename: string = 'export.xlsx',
  options?: {
    sheetName?: string;
    columnWidths?: number[];
    autoFilter?: boolean;
    freezeHeader?: boolean;
  }
): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Auto-detect field metadata from first row
  const headers = Object.keys(data[0] || {});
  const fieldMetadata = headers.map(key => {
    const sampleValue = data.find(row => row[key] !== null && row[key] !== undefined)?.[key];
    if (sampleValue === undefined) {
      return { key, type: 'string', isBoolean: false, isEnum: false, isNumeric: false };
    }
    
    const fieldMeta = detectFieldType(key, sampleValue);
    return {
      key,
      type: fieldMeta.type,
      isBoolean: fieldMeta.isBoolean,
      isEnum: fieldMeta.isEnum,
      isNumeric: fieldMeta.isNumeric
    };
  });

  exportToExcel(data, filename, {
    ...options,
    enhancedFormatting: true,
    fieldMetadata
  });
};

/**
 * Export multiple sheets to Excel
 * Full implementation with multiple worksheets
 */
export const exportToExcelMultiSheet = (
  sheets: Record<string, any[]>,
  filename: string = 'export.xlsx',
  options?: {
    columnWidths?: Record<string, number[]>;
    autoFilter?: boolean;
    freezeHeaders?: boolean;
  }
): void => {
  const { columnWidths, autoFilter = true, freezeHeaders = true } = options || {};

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Add each sheet
  Object.entries(sheets).forEach(([sheetName, data]) => {
    if (data.length === 0) {
      console.warn(`Sheet "${sheetName}" has no data, skipping`);
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);

    // Apply column widths for this sheet
    if (columnWidths && columnWidths[sheetName]) {
      worksheet['!cols'] = columnWidths[sheetName].map((width) => ({ wch: width }));
    } else {
      // Auto-calculate
      const headers = Object.keys(data[0] || {});
      worksheet['!cols'] = headers.map((header) => {
        const maxLength = Math.max(
          header.length,
          ...data.map((row) => String(row[header] || '').length).slice(0, 100)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });
    }

    // Add autofilter
    if (autoFilter) {
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
    }

    // Freeze header
    if (freezeHeaders) {
      worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };
    }

    // Add to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31)); // Excel limit
  });

  // Generate and download
  if (workbook.SheetNames.length > 0) {
    XLSX.writeFile(workbook, filename);
  } else {
    console.warn('No sheets to export');
  }
};

/** Column definition for `exportToPDF`; falls back to raw object keys when omitted. */
export interface PDFExportColumn {
  key: string;
  label: string;
}

/** Truncates text with an ellipsis so it never overlaps the next column. */
function truncateToWidth(pdf: jsPDF, text: string, maxWidth: number): string {
  if (pdf.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && pdf.getTextWidth(`${truncated}…`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated.length > 0 ? `${truncated}…` : '';
}

/**
 * Export tabular data to a paginated PDF (header row + alternating row shading).
 * Generic, data-driven export — same shape as `exportToCSV`/`exportToExcel` — for
 * use anywhere a lightweight tabular PDF (not a chart screenshot) is needed.
 */
export const exportToPDF = (
  data: any[],
  filename: string = 'export.pdf',
  options?: {
    title?: string;
    columns?: PDFExportColumn[];
    orientation?: 'portrait' | 'landscape';
  }
): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const { title, orientation = 'landscape' } = options || {};
  const columns: PDFExportColumn[] =
    options?.columns && options.columns.length > 0
      ? options.columns
      : Object.keys(data[0]).map((key) => ({ key, label: key }));

  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 32;
  const rowHeight = 18;
  const usableWidth = pageWidth - margin * 2;
  const colWidth = usableWidth / columns.length;
  let y = margin;

  const drawHeader = () => {
    pdf.setFillColor(66, 114, 196);
    pdf.rect(margin, y, usableWidth, rowHeight, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    columns.forEach((col, i) => {
      const x = margin + i * colWidth + 4;
      pdf.text(truncateToWidth(pdf, String(col.label), colWidth - 8), x, y + rowHeight - 6);
    });
    y += rowHeight;
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 30, 30);
  };

  if (title) {
    pdf.setFontSize(15);
    pdf.setTextColor(20, 20, 20);
    pdf.text(title, margin, y);
    y += 20;
  }
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.text(`Generated ${new Date().toLocaleString()} · ${data.length} record${data.length === 1 ? '' : 's'}`, margin, y);
  y += 14;

  drawHeader();

  data.forEach((row, rowIndex) => {
    if (y + rowHeight > pageHeight - margin) {
      pdf.addPage();
      y = margin;
      drawHeader();
    }
    if (rowIndex % 2 === 1) {
      pdf.setFillColor(245, 246, 248);
      pdf.rect(margin, y, usableWidth, rowHeight, 'F');
    }
    pdf.setFontSize(8);
    pdf.setTextColor(30, 30, 30);
    columns.forEach((col, i) => {
      const raw = row[col.key];
      const text = raw === null || raw === undefined ? '' : String(raw);
      const x = margin + i * colWidth + 4;
      pdf.text(truncateToWidth(pdf, text, colWidth - 8), x, y + rowHeight - 6);
    });
    y += rowHeight;
  });

  pdf.save(filename);
};

/**
 * Export chart/visualization to image then PDF
 */
export const exportChartToPDF = async (
  elementId: string,
  filename: string = 'chart.pdf',
  options?: {
    width?: number;
    height?: number;
    quality?: number;
  }
): Promise<void> => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return;
  }

  const { width = 1200, height = 800, quality = 1 } = options || {};

  try {
    // Convert to canvas
    const canvas = await html2canvas(element, {
      width,
      height,
      scale: quality,
      useCORS: true,
    });

    // Convert to PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'px', [width, height]);
    pdf.addImage(imgData, 'PNG', 0, 0, width, height);
    pdf.save(filename);
  } catch (error) {
    console.error('Error exporting chart to PDF:', error);
    throw error;
  }
};

/**
 * Format entities for export (flatten structure)
 */
export const formatEntitiesForExport = (entities: any[]) => {
  return entities.map((entity) => ({
    entity_id: entity.id,
    entity_type: entity.entity_type,
    source_artifact_id: entity.source_artifact_id || '',
    created_at: entity.created_at || '',
    ...entity.data,
  }));
};

/**
 * Export entities with full Excel formatting
 */
export const exportEntitiesWithFormatting = (
  entities: any[],
  filename: string,
  options?: {
    includeMetadata?: boolean;
    groupByType?: boolean;
  }
): void => {
  const { includeMetadata = true, groupByType = false } = options || {};

  if (!groupByType) {
    // Single sheet export
    const formatted = formatEntitiesForExport(entities);
    exportToExcel(formatted, filename, {
      sheetName: 'Entities',
      autoFilter: true,
      freezeHeader: true,
    });
  } else {
    // Multi-sheet export grouped by entity type
    const groupedEntities: Record<string, any[]> = {};
    
    entities.forEach((entity) => {
      const type = entity.entity_type || 'Unknown';
      if (!groupedEntities[type]) {
        groupedEntities[type] = [];
      }
      groupedEntities[type].push({
        id: entity.id,
        created_at: entity.created_at,
        ...entity.data,
      });
    });

    // Add metadata sheet if requested
    if (includeMetadata) {
      groupedEntities['_Metadata'] = [
        {
          export_date: new Date().toISOString(),
          total_entities: entities.length,
          entity_types: Object.keys(groupedEntities).filter((k) => k !== '_Metadata').length,
          format: 'TraceKit Entity Export',
        },
      ];
    }

    exportToExcelMultiSheet(groupedEntities, filename);
  }
};

/**
 * Export entities with enhanced formatting
 */
export async function exportEntitiesByType(
  entities: any[],
  filename: string,
  options?: {
    groupByType?: boolean;
    enhancedFormatting?: boolean;
  }
): Promise<void> {
  const { groupByType = false, enhancedFormatting = true } = options || {};

  if (groupByType) {
    // Group by entity type
    const grouped: Record<string, any[]> = {};
    entities.forEach((entity) => {
      const type = entity.entity_type || 'Unknown';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(formatEntitiesForExport([entity])[0]);
    });

    // Export each type as a separate sheet with formatting
    const workbook = XLSX.utils.book_new();
    
    for (const [type, data] of Object.entries(grouped)) {
      if (data.length === 0) continue;
      
      const headers = Object.keys(data[0] || {});
      const fieldMetadata = headers.map(key => {
        const sampleValue = data.find(row => row[key] !== null && row[key] !== undefined)?.[key];
        if (sampleValue === undefined) {
          return { key, type: 'string', isBoolean: false, isEnum: false, isNumeric: false };
        }
        const fieldMeta = detectFieldType(key, sampleValue);
        return {
          key,
          type: fieldMeta.type,
          isBoolean: fieldMeta.isBoolean,
          isEnum: fieldMeta.isEnum,
          isNumeric: fieldMeta.isNumeric
        };
      });

      const worksheet = enhancedFormatting 
        ? createFormattedWorksheet(data, fieldMetadata)
        : XLSX.utils.json_to_sheet(data);

      // Auto-calculate column widths
      worksheet['!cols'] = headers.map((header) => {
        const maxLength = Math.max(
          header.length,
          ...data.map((row) => String(row[header] || '').length).slice(0, 100)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });

      // Add autofilter and freeze
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
      worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };

      XLSX.utils.book_append_sheet(workbook, worksheet, type.substring(0, 31));
    }

    XLSX.writeFile(workbook, filename);
  } else {
    // Single sheet export
    const formatted = formatEntitiesForExport(entities);
    if (enhancedFormatting) {
      exportToExcelFormatted(formatted, filename, {
        autoFilter: true,
        freezeHeader: true,
      });
    } else {
      exportToExcel(formatted, filename, {
        autoFilter: true,
        freezeHeader: true,
      });
    }
  }
}

/**
 * Export entities to Excel with enhanced formatting
 */
export async function exportEntitiesByTypeToExcel(
  entities: any[],
  filename: string,
  options?: {
    groupByType?: boolean;
    enhancedFormatting?: boolean;
  }
): Promise<void> {
  return exportEntitiesByType(entities, filename, { ...options, enhancedFormatting: options?.enhancedFormatting ?? true });
}
